<script setup lang="ts">
import { computed, inject } from "vue";
import { OfferService } from "../services/offer.service";
import { SITEURL } from "@libTs/cms.globals";
import { itemCodeToUrlSlug } from "../helpers/url.helper";
import { vClickOutside } from "../directives/v-click-outside";

const offerSvc = inject<OfferService>("offerSvc") as OfferService;

const formatAsCurrency = (value: number) => {
    return new Intl.NumberFormat("de-CH", {
        style: "currency",
        currency: "CHF",
    }).format(value);
};

const totalPrice = computed(() => {
    return offerSvc.list.reduce((total, item) => {
        return total + (item.Preis ?? 0) * (item.Quantity ?? 1);
    }, 0);
});
</script>

<template>
    <div
        class="relative border-b-4 h-full flex items-center w-6 justify-center border-t-transparent border-t-4"
        :class="{
            'border-b-primary': offerSvc.openOfferList,
            'border-b-transparent': !offerSvc.openOfferList,
        }"
        v-click-outside="() => (offerSvc.openOfferList = false)"
    >
        <button
            class="btn-list"
            @click="offerSvc.openOfferList = !offerSvc.openOfferList"
            title="Offer list"
        ></button>
        <div
            v-if="offerSvc.openOfferList"
            class="absolute top-20 right-0 w-auto min-w-[550px] bg-white shadow-lg z-50 pt-8 pb-8 px-6"
        >
            <button
                @click="offerSvc.openOfferList = false"
                class="absolute top-6 right-6 text-grey-500 hover:text-grey-700"
            >
                <i class="fal fa-times text-f3"></i>
            </button>
            <div
                class="pb-4 border-b border-grey-200 flex justify-between items-center"
            >
                <h3 class="text-f4 font-bold">Offer list</h3>
            </div>
            <div v-if="offerSvc.list.length === 0" class="py-4">
                Your offer list is empty.
            </div>
            <ul v-else class="">
                <li
                    v-for="(item, index) in offerSvc.list"
                    :key="index"
                    class="py-4 border-b border-grey-200 flex items-start"
                >
                    <div class="flex items-start w-full gap-5">
                        <img
                            v-if="item.ImageURL"
                            :src="item.ImageURL"
                            alt="Item Image"
                            class="w-44 h-24 object-contain mr-4 rounded-sm shrink-0 grow-0"
                        />
                        <div
                            class="bg-grey-30 rounded-sm w-44 h-24 mr-4 flex items-center justify-center shrink-0 grow-0"
                            v-else
                        >
                            <img
                                :src="SITEURL + 'assets/icons/no-photo.svg'"
                                alt="No Image Available"
                                class="w-4 h-4 object-contain"
                            />
                        </div>
                        <div class="flex flex-col w-full gap-4 h-full">
                            <div
                                class="flex flex-row justify-between items-start gap-4"
                            >
                                <a
                                    :href="'/product/' + itemCodeToUrlSlug(item.ItemCode)"
                                    class="flex-grow"
                                >
                                    <h4
                                        class="text-f6 font-bold break-word hyphens-auto"
                                    >
                                        {{ item.ItemName }}
                                    </h4>
                                </a>
                                <div class="flex flex-row items-center gap-2">
                                    <button
                                        class="w-8 h-8 bg-grey-50 hover:bg-grey-10 rounded-s font-bold text-f6"
                                        @click="
                                            item.Quantity > 1
                                                ? item.Quantity--
                                                : null
                                        "
                                    >
                                        -
                                    </button>
                                    <input
                                        type="number"
                                        v-model="item.Quantity"
                                        class="w-12 text-center border-none text-f6 font-bold quantity-input"
                                        min="1"
                                    />
                                    <button
                                        class="w-8 h-8 bg-grey-50 hover:bg-grey-100 rounded-s font-bold text-f6"
                                        @click="item.Quantity++"
                                    >
                                        +
                                    </button>
                                </div>
                                <button
                                    class=""
                                    @click.stop="
                                        offerSvc.remove({
                                            ItemCode: item.ItemCode,
                                        })
                                    "
                                >
                                    <i class="far fa-trash"></i>
                                </button>
                            </div>
                            <div class="flex justify-between items-center">
                                <div class="text-f7">
                                    Art. Nr. {{ item.ItemCode }}
                                </div>
                                <div class="text-f5 font-bold">
                                    {{ formatAsCurrency(item.Preis) }}
                                </div>
                            </div>
                        </div>
                    </div>
                </li>
            </ul>
            <div class="" v-if="offerSvc.list.length > 0">
                <div class="py-8 flex justify-between items-center">
                    <div class="text-f4 font-bold">Total</div>
                    <div class="text-f4 font-bold">
                        {{ formatAsCurrency(totalPrice) }}
                    </div>
                </div>
                <div class="">
                    <a href="/shop/offer" class="btn-cb w-full">
                        <span class="font-bold">Create offer PDF</span>
                        <i class="fas fa-file-alt"></i>
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>
